import { notification, Spin } from 'antd/lib'
import { Loader } from 'Components/common/Loader/Loader'
import { PAGE_TITLES } from 'Constants/page.constants'
import { StudioProvider } from 'Contexts/StudioContext'
import { useUserDetails } from 'Contexts/UserDetailsContext'
import { Studio } from 'Core/Studio'
import { useExamAssignment } from 'Hooks/exams/use-exam-assignment'
import { useExamAttempt } from 'Hooks/exams/use-exam-attempt'
import { useTitle } from 'Hooks/use-title'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { submitAttempt } from 'Services/exams.service'
import { ExamType } from 'Types/Exams.types'
import { ContextConfigType, ContextType } from 'Types/Tasks.types'

export const ExamTaskPage: React.FC = () => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number | undefined>()
    useTitle(PAGE_TITLES.EXAM_TASK)

    const navigate = useNavigate()
    const userDetails = useUserDetails()
    const { data, isLoading: isLoadingAttempt, timeLimit } = useExamAttempt()

    const { studioConfig, isLoading, id } = useExamAssignment(
        currentQuestionIndex !== undefined ? data?.answerIds[currentQuestionIndex].id : undefined
    )

    useEffect(() => {
        if (data?.questionIndex !== undefined) {
            setCurrentQuestionIndex(data.questionIndex)
        }
    }, [data?.questionIndex])

    const contextConfig: ContextConfigType | undefined = useMemo(() => {
        if (!data) return
        return {
            type: ContextType.ExamTask,
            metaData: {
                examInfo: {
                    totalQuestions: data.answerIds.length,
                    currentQuestion: currentQuestionIndex ? currentQuestionIndex + 1 : 1,
                    timeLimit,
                    answerId: data.answerIds[currentQuestionIndex ?? 0].id,
                },
                isTrainingMode: data.examType === ExamType.TRAINING,
                isArchived: data.isArchived,
            },
        }
    }, [currentQuestionIndex, data, timeLimit])

    const submitExamAttempt = useCallback(async () => {
        if (!data || !userDetails) return
        try {
            await submitAttempt(data.attemptId)
            navigate(`/exam/${data.examId}/attempt/${data.attemptId}/result`)
        } catch (error) {
            notification.error({
                message: 'Failed to submit exam attempt',
                description: 'Please try again',
                placement: 'bottomLeft',
            })
        }
    }, [data, navigate, userDetails])

    const submitAndNext = useCallback(() => {
        if (!data) return

        const nextQuestionIndex = currentQuestionIndex ? currentQuestionIndex + 1 : 1
        if (nextQuestionIndex === data.answerIds.length) {
            submitExamAttempt()
        } else {
            setCurrentQuestionIndex(nextQuestionIndex)
        }
    }, [data, currentQuestionIndex, setCurrentQuestionIndex, submitExamAttempt])

    const onExitTask = useCallback(() => {
        submitExamAttempt()
    }, [submitExamAttempt])

    if (!studioConfig || !contextConfig || isLoadingAttempt || isLoading) {
        return <Spin indicator={<Loader title={'Loading task...'} />} />
    }

    const studio = new Studio({
        ...studioConfig,
        context: contextConfig,
        onNextTask: submitAndNext,
        onExitTask,
    })

    return (
        <div data-testid="exam-task-wrapper">
            <StudioProvider key={id} value={studio}>
                {studio.workbench.element}
            </StudioProvider>
        </div>
    )
}
